import { ReportsViewEntityBalanceMatrix } from "@/app/app/_/views/ReportsView/ReportsViewWidget/ReportsViewEntityBalanceMatrix";
import { ReportsViewGroupedSummaryWidget } from "@/app/app/_/views/ReportsView/ReportsViewWidget/ReportsViewGroupedSummaryWidget";
import { ReportsViewMetricWidget } from "@/app/app/_/views/ReportsView/ReportsViewWidget/ReportsViewMetricWidget";
import { SharedReportTreeBody } from "@/app/share/report/SharedReportTreeBody";
import type { ReportWidgetShapeFragment } from "@/__generated__/graphql";
import { Chart } from "@/components/organisms/Chart";
import { identifierAndTypenameToId } from "@/objects/@utils";
import { iconsByTypename } from "@/objects/@icons";
import { Utils } from "@/utils";
import { Typography } from "@/components/ui/typography";

/**
 * What a widget came to, drawn.
 *
 * A widget is a question the server answers in one of four shapes - a chart, a
 * table, a tree, a matrix - and how it is drawn follows from the shape and the
 * presentation, not from what it happens to be of. Before this there was a
 * branch per construct and presentation pair, and a GraphQL type behind each
 * one; a new kind of widget now needs nothing here at all.
 */

/** How a chart shape is drawn, from the presentation the widget was saved with. */
function chartTypeFor(
  presentation: string | null | undefined,
): "bar" | "line" | "pie" {
  switch (presentation) {
    case "PIE_CHART":
      return "pie";
    case "LINE_CHART":
      return "line";
    default:
      return "bar";
  }
}

/** A chart's points as a strip of figures, for a widget drawn that way. */
function KpiStrip({
  chart,
}: {
  chart: NonNullable<ReportWidgetShapeFragment["chart"]>;
}) {
  // A strip of one point is a strip of its figures - a net cash and what it
  // was last month are two readings of one moment, and mapping points to
  // figures showed the first and dropped the rest. Several points are several
  // moments, and then it is the points that are the figures.
  const items =
    chart.points.length === 1
      ? chart.points[0].values.map((value) => ({
          label: value.key,
          value: Number(value.value ?? 0),
        }))
      : chart.points.map((point) => ({
          label: point.label,
          value: Number(point.values[0]?.value ?? 0),
        }));

  if (items.length === 0) {
    return (
      <Typography level="body-sm" className="text-muted-foreground/80">
        Nothing to show for the selected period.
      </Typography>
    );
  }

  if (items.length === 1) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 min-h-0 w-full">
        <ReportsViewMetricWidget value={items[0].value} />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center flex-1 min-h-0 w-full">
      <ReportsViewMetricWidget items={items} />
    </div>
  );
}

/** Where a row keeps the node id of the record each of its levels names. */
const NODE_ID_SUFFIX = ":node";

/**
 * A table shape, grouped by everything it is not a figure of.
 *
 * A table says what each of its columns holds, and that is the whole layout:
 * the columns that are not money are the levels a row is grouped under, in the
 * order they are given - bank accounts arrive as entity, institution, account
 * - and the money column is what each group totals. A column that is of a kind
 * of record says so, so each level carries that record's own icon rather than
 * a generic branch and leaf.
 */
function ReportWidgetTable({
  table,
  isLinked,
}: {
  table: NonNullable<ReportWidgetShapeFragment["table"]>;
  /** In-app rows open the record they name; a shared link cannot. */
  isLinked: boolean;
}) {
  const money = table.columns.filter((column) => column.columnKind === "money");
  const levels = table.columns.filter(
    (column) => column.columnKind !== "money",
  );
  const valueColumn = (money[0] ?? table.columns[table.columns.length - 1])
    ?.key;

  // A table of groups the database already made carries no levels of its own:
  // each row is named rather than spelled out in a cell, so that is the level.
  const groupingKeys = levels.length
    ? levels.map((level) => level.key)
    : ["label"];

  const rows = table.rows.map((row) => ({
    id: row.rowKey,
    label: row.label,
    ...Object.fromEntries(
      levels.flatMap((level) => {
        const cell = row.cells.find((one) => one.columnKey === level.key);
        return [
          [level.key, cell?.formattedAmount ?? ""],
          // The record the level names, kept beside its text so a row can be
          // opened from whichever level was clicked.
          [
            `${level.key}${NODE_ID_SUFFIX}`,
            cell?.identifier && level.typename
              ? identifierAndTypenameToId(cell.identifier, level.typename)
              : null,
          ],
        ];
      }),
    ),
    value: Number(
      row.cells.find((cell) => cell.columnKey === valueColumn)?.amount ?? 0,
    ),
  }));

  if (rows.length === 0) {
    return (
      <Typography level="body-sm" className="text-muted-foreground/80">
        Nothing to show for the selected period.
      </Typography>
    );
  }

  // Averages, largest and smallest do not add up, so a heading over them has
  // no figure to show: the sum of the averages under it would be a number
  // that is true of nothing.
  const addsUp = !/^(avg|min|max)\./.test(valueColumn ?? "");

  const iconFor = Object.fromEntries(
    table.columns.map((column) => [
      column.key,
      iconsByTypename[column.typename as keyof typeof iconsByTypename] ?? null,
    ]),
  );

  return (
    <ReportsViewGroupedSummaryWidget
      data={rows}
      numericKeys={["value"]}
      operation="sum"
      type=""
      // The levels are the server's own columns, so they are keys of a row
      // by construction rather than by anything the types can see.
      groupingKeys={groupingKeys as Array<keyof (typeof rows)[number]>}
      rowIcon={(groupKey) => iconFor[groupKey] ?? null}
      hideGroupTotals={!addsUp}
      getGroupNodeId={
        isLinked
          ? (groupKey, row) =>
              (row[`${groupKey}${NODE_ID_SUFFIX}`] as string | null) ?? null
          : undefined
      }
      formatValue={(value) => Utils.fCurrency(value)}
      size="sm"
      embedded
    />
  );
}

export function ReportWidgetBody({
  data,
  presentation,
  fallbackLabel,
  isLinked = false,
  rows,
}: {
  data: ReportWidgetShapeFragment | null | undefined;
  presentation: string | null | undefined;
  fallbackLabel?: string;
  /** What the widget counts, named - so a chart can say what it is hiding. */
  rows?: string;
  /** In-app widgets link records into the node panel; shared reports cannot. */
  isLinked?: boolean;
}) {
  const nothing = fallbackLabel ? (
    <Typography level="body-sm" className="text-muted-foreground/80">
      {fallbackLabel}
    </Typography>
  ) : null;

  if (!data) return nothing;

  if (data.chart) {
    if (presentation === "KPI_STRIP") {
      return <KpiStrip chart={data.chart} />;
    }
    if (!data.chart.points.length) {
      return (
        <Typography level="body-sm" className="text-muted-foreground/80">
          No chart data for the selected period.
        </Typography>
      );
    }
    return (
      // The card body is as tall as the card leaves it - a title that wraps to
      // a second line takes a line off the body - so the chart is drawn to the
      // room there is rather than to a height of its own, which the body could
      // only answer by scrolling.
      <div className="flex-1 min-h-55 flex w-full">
        <Chart
          data={data.chart}
          type={chartTypeFor(presentation)}
          size="sm"
          hideTitle
          fill
          rows={rows}
        />
      </div>
    );
  }

  if (data.table) {
    return <ReportWidgetTable table={data.table} isLinked={isLinked} />;
  }

  if (data.tree) {
    const roots = data.tree.roots ?? [];
    if (roots.length === 0) return nothing;
    // A statement carries a column per period; read with none, every figure
    // in it falls under one heading no row has and draws as a dash.
    return (
      <SharedReportTreeBody roots={roots} columns={data.tree.columns ?? []} />
    );
  }

  if (data.matrix) {
    return (
      <div className="flex-1 min-h-0 w-full flex flex-col overflow-hidden">
        <ReportsViewEntityBalanceMatrix
          entities={data.matrix.entities.map((entity) => ({
            identifier: entity.identifier,
            label: entity.label,
          }))}
          cells={data.matrix.cells.map((cell) => ({
            debtorIdentifier: cell.debtorIdentifier,
            creditorIdentifier: cell.creditorIdentifier,
            amount: Number(cell.amount ?? 0),
          }))}
        />
      </div>
    );
  }

  return nothing;
}
